import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

interface EnhancedSEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl: string;
  ogImage?: string;
  structuredData?: object;
  alternateLanguages?: { lang: string; url: string }[];
}

export const EnhancedSEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png",
  structuredData,
  alternateLanguages = []
}: EnhancedSEOHeadProps) => {
  const { language } = useLanguage();
  const fullCanonicalUrl = `https://hundeschulenfinder.de${canonicalUrl}`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `https://hundeschulenfinder.de${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Language Alternates */}
      {alternateLanguages.map(({ lang, url }) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`https://hundeschulenfinder.de${url}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={fullCanonicalUrl} />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:locale" content={language === 'de' ? 'de_DE' : 'en_US'} />
      <meta property="og:site_name" content="Hundeschulenfinder" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Mobile */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=yes" />
      
      {/* Favicon */}
      <link rel="icon" href="/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
